/**
 * The primary navigation.
 *
 * One list, rendered by the rail, the drawer and the bottom bar — so the three
 * cannot disagree about what exists or what it is called.
 *
 * Items carry a `group`. The rail renders those as headings (Pipeline ·
 * Relationships · Insight), which is how the reference prototype organises a
 * nine-item menu; the bottom bar ignores them, because four items need no
 * grouping. Grouping lives on the item rather than in a separate structure so
 * that adding a destination cannot leave it out of a group by omission.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 5.7, 6.5.5, §7.3
 */
import type { Role } from '@nexus/contracts';

import type { IconName } from '../ui/icon-names';

/**
 * The rail's section headings, in render order.
 *
 * An ordered tuple rather than a set: the rail iterates this to decide which
 * heading comes first, so the order is data, not a sort applied afterwards.
 */
export const NAV_GROUPS = ['pipeline', 'relationships', 'insight'] as const;

export type NavGroup = (typeof NAV_GROUPS)[number];

export interface NavItem {
  /** Key into the `nav` translation namespace. */
  labelKey: string;
  path: string;
  icon: IconName;
  /** Minimum role. Items above a user's role are not rendered. */
  role: Role;
  /** Shown in the mobile bottom bar, which holds far fewer items. */
  primary: boolean;
  /** The rail section this item belongs to. */
  group: NavGroup;
  /**
   * Key into the counts object the shell is given, for the figure shown at the
   * item's trailing edge. Omitted where a count would be meaningless — the
   * dashboard is not a list of anything.
   */
  countKey?: 'startups' | 'pilots' | 'customers' | 'assets' | 'contacts';
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    labelKey: 'nav.dashboard',
    path: '/dashboard',
    icon: 'gauge',
    role: 'VIEWER',
    primary: true,
    group: 'pipeline',
  },
  {
    labelKey: 'nav.startups',
    path: '/startups',
    icon: 'target',
    role: 'VIEWER',
    primary: true,
    group: 'pipeline',
    countKey: 'startups',
  },
  {
    labelKey: 'nav.pilots',
    path: '/pilots',
    icon: 'checklist',
    role: 'VIEWER',
    primary: true,
    group: 'pipeline',
    countKey: 'pilots',
  },
  {
    /* EDITOR-gated: matchmaking proposes actions a viewer cannot take. */
    labelKey: 'nav.matchmaker',
    path: '/matchmaker',
    icon: 'search',
    role: 'EDITOR',
    primary: false,
    group: 'pipeline',
  },
  {
    labelKey: 'nav.customers',
    path: '/customers',
    icon: 'industry',
    role: 'VIEWER',
    primary: true,
    group: 'relationships',
    countKey: 'customers',
  },
  {
    labelKey: 'nav.assets',
    path: '/assets',
    icon: 'pin',
    role: 'VIEWER',
    primary: false,
    group: 'relationships',
    countKey: 'assets',
  },
  {
    labelKey: 'nav.contacts',
    path: '/contacts',
    icon: 'contract',
    role: 'VIEWER',
    primary: false,
    group: 'relationships',
    countKey: 'contacts',
  },
  {
    labelKey: 'nav.alerts',
    path: '/alerts',
    icon: 'alert',
    role: 'VIEWER',
    primary: false,
    group: 'insight',
  },
  {
    labelKey: 'nav.analytics',
    path: '/reports',
    icon: 'trend-up',
    role: 'VIEWER',
    primary: false,
    group: 'insight',
  },
  {
    labelKey: 'nav.admin',
    path: '/admin',
    icon: 'shield',
    role: 'ADMIN',
    primary: false,
    group: 'insight',
  },
];
